// frontend/src/pages/PublicProfilePage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wifi, WifiOff, ExternalLink, Film, Tv, Play } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import ClipPlayerModal from '../components/ClipPlayerModal';
import { safeUrl, safeExternalUrl, sanitizeTwitchUsername, sanitizeImageUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Social platform config — icon SVGs + colors
const SOCIAL_CONFIG = {
  twitch: {
    label: 'Twitch',
    color: '#9146FF',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#9146FF"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>,
  },
  instagram: {
    label: 'Instagram',
    color: '#E1306C',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#E1306C"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  },
  twitter: {
    label: 'X / Twitter',
    color: '#fff',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  },
  youtube: {
    label: 'YouTube',
    color: '#FF0000',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  },
  tiktok: {
    label: 'TikTok',
    color: '#fff',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/></svg>,
  },
  discord: {
    label: 'Discord',
    color: '#5865F2',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>,
  },
  website: {
    label: 'Website',
    color: '#aaa',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  },
};

const PublicProfilePage = () => {
  const { username }  = useParams();
  const { authToken } = useContext(AuthContext);
  const navigate      = useNavigate();
  const [profile, setProfile]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [privateProfile, setPrivateProfile] = useState(false);
  const [activeClip, setActiveClip]         = useState(null);

  const font = {
    mono:      '"JetBrains Mono", monospace',
    condensed: '"Barlow Condensed", sans-serif',
    body:      '"Barlow", sans-serif',
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const res = await fetch(`${BACKEND_URL}/api/profile/${username}`, { headers });
        if (res.status === 403) { setPrivateProfile(true); return; }
        if (res.status === 404) { setError('Profile not found.'); return; }
        if (!res.ok) throw new Error(`${res.status}`);
        setProfile(await res.json());
      } catch (e) {
        setError('Could not load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username, authToken]);

  const formatNumber   = (n) => { if (!n) return '0'; if (n >= 1000000) return (n/1000000).toFixed(1)+'M'; if (n >= 1000) return (n/1000).toFixed(1)+'K'; return String(n); };
  const formatDate     = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';
  const formatDuration = (dur) => { if (!dur) return ''; const m = dur.match(/(\d+h)?(\d+m)?(\d+s)?/); if (!m) return dur; return ((m[1]?m[1].replace('h','')+'h ':'')+(m[2]?m[2].replace('m','')+'m':'')).trim()||dur; };

  const getBadges = (p) => [
    { id:'beta',      label:'Beta Tester',    unlocked:p?.user?.is_beta === true,            icon:'🧪' },
    { id:'member',    label:'1-Year Member',  unlocked:true,                                icon:'🏅' },
    { id:'followers', label:'1K+ Followers',  unlocked:(p?.followers||0)>=1000,             icon:'👥' },
    { id:'active',    label:'Active Streamer', unlocked:(p?.clips?.length||0)>0,            icon:'📡' },
    { id:'live',      label:'Live Streamer',   unlocked:p?.is_live,                         icon:'🔴' },
    { id:'vod',       label:'VOD Library',     unlocked:(p?.videos?.length||0)>=5,          icon:'🎬' },
    { id:'bgn',       label:'BGN Verified',    unlocked:true,                               icon:'✅' },
  ];

  const getTopCategories = (videos) => {
    if (!videos?.length) return [];
    const counts = {};
    videos.forEach(v => { if (v.game_name) counts[v.game_name] = (counts[v.game_name]||0)+1; });
    const max = Math.max(...Object.values(counts));
    return Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([name,count])=>({ name, count, pct:Math.round((count/max)*100) }));
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ background:'#000', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:36, height:36, border:'2px solid #222', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 1rem' }} />
        <p style={{ fontFamily:font.mono, fontSize:'0.7rem', color:'#555', letterSpacing:'0.1em' }}>LOADING PROFILE...</p>
      </div>
    </div>
  );

  // ── Private ──────────────────────────────────────────────────────────────────
  if (privateProfile) return (
    <div style={{ background:'#000', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');`}</style>
      <div style={{ textAlign:'center', maxWidth:400, padding:'0 2rem' }}>
        <div style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#444', letterSpacing:'0.2em', marginBottom:'1rem' }}>// PRIVATE PROFILE</div>
        <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'2.5rem', color:'#fff', marginBottom:'0.5rem' }}>@{username}</div>
        <p style={{ fontFamily:font.body, fontWeight:300, fontSize:'0.9rem', color:'#555', lineHeight:1.6, marginBottom:'2rem' }}>
          This profile is only visible to BGN members. Connect your Twitch account to view it.
        </p>
        <button onClick={() => navigate('/account')} style={{ background:'#fff', color:'#000', border:'none', padding:'0.75rem 2rem', fontFamily:font.mono, fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', cursor:'pointer' }}>
          JOIN BGN
        </button>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) return (
    <div style={{ background:'#000', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <p style={{ fontFamily:font.mono, fontSize:'0.8rem', color:'#ff4444', marginBottom:'1rem' }}>{error}</p>
        <button onClick={() => navigate(-1)} style={{ background:'none', border:'1px solid #333', color:'#fff', padding:'0.5rem 1.2rem', fontFamily:font.mono, fontSize:'0.65rem', cursor:'pointer' }}>GO BACK</button>
      </div>
    </div>
  );

  const { user, followers, is_live, stream, videos, channel, clips, featured_clip_ids, social_links } = profile;

  const sortedClips = [
    ...(featured_clip_ids||[]).map(id => clips?.find(c=>c.id===id)).filter(Boolean),
    ...(clips||[]).filter(c=>!(featured_clip_ids||[]).includes(c.id)),
  ];

  const topCats    = getTopCategories(videos);
  const badges     = getBadges(profile);
  const memberYear = user?.created_at ? new Date(user.created_at).getFullYear() : '—';
  const activeSocials = Object.entries(social_links||{}).filter(([,v])=>v);

  const sectionTitle = { fontFamily:font.mono, fontSize:'0.65rem', color:'#FFD700', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'1rem', paddingBottom:'0.5rem', borderBottom:'1px solid #222' };
  const cardStyle    = { background:'#111', border:'1px solid #222', padding:'1.25rem' };
  const cardTitle    = { fontFamily:font.mono, fontSize:'0.65rem', color:'#FFD700', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'0.75rem' };

  return (
    <div style={{ background:'#000', color:'#fff', fontFamily:font.body }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(1.08)} }
        @keyframes spin  { to{transform:rotate(360deg)} }
        .bgn-clip-card:hover img { filter:grayscale(0%) !important; }
        .bgn-clip-card:hover     { border-color:#FFD700 !important; }
        .bgn-clip-card:hover .bgn-play-overlay { opacity:1 !important; }
        .bgn-vod:hover .bgn-overlay { opacity:1 !important; }
        .bgn-social-link:hover   { border-color:#FFD700 !important; background:#1a1400 !important; }
        @media(max-width:768px){
          .bgn-body     { grid-template-columns:1fr !important; }
          .bgn-left-col { border-right:none !important; border-bottom:1px solid #222; }
        }
      `}</style>

      {/* HEADER */}
      <div style={{ borderBottom:'1px solid #222', padding:'2rem 2.5rem', display:'flex', alignItems:'center', gap:'2rem', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flexShrink:0 }}>
          {is_live && <div style={{ position:'absolute', inset:-6, borderRadius:'50%', border:'2px solid #ff4444', animation:'pulse 1.8s ease-in-out infinite' }} />}
          <img src={sanitizeImageUrl(user?.profile_image_url) ?? '/images/default-avatar.png'} alt={user?.display_name} style={{ width:120, height:120, borderRadius:'50%', border:'3px solid #FFD700', filter:'grayscale(100%)', display:'block' }} />
        </div>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'clamp(1.8rem, 5vw, 4rem)', letterSpacing:'-0.02em', lineHeight:1, whiteSpace:'nowrap', marginBottom:'0.5rem' }}>
            @{user?.display_name}
          </div>
          <div style={{ marginBottom:'0.5rem' }}>
            {is_live
              ? <span style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#ff4444', color:'#fff', fontSize:'0.7rem', fontFamily:font.mono, fontWeight:700, letterSpacing:'0.1em', padding:'3px 10px', borderRadius:2 }}><Wifi size={10} /> LIVE</span>
              : <span style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#222', color:'#888', fontSize:'0.7rem', fontFamily:font.mono, fontWeight:700, letterSpacing:'0.1em', padding:'3px 10px', borderRadius:2 }}><WifiOff size={10} /> OFFLINE</span>
            }
          </div>
          {is_live && stream?.title && <p style={{ fontFamily:font.body, fontSize:'0.85rem', color:'#aaa', marginBottom:'0.5rem' }}>{stream.title}</p>}
          <div style={{ display:'flex', gap:'1.5rem', marginTop:'1rem', flexWrap:'wrap' }}>
            {((() => {
              const totalViews = [...(clips||[]), ...(videos||[])].reduce((sum, i) => sum + (i.view_count||0), 0);
              const stats = [['Followers', formatNumber(followers)], ['Since', memberYear]];
              if (totalViews > 0) stats.splice(1, 0, ['Twitch Views', formatNumber(totalViews)]);
              return stats;
            })()).map(([label, value]) => (
              <div key={label} style={{ background:'#111', border:'1px solid #222', padding:'0.75rem 1.25rem', minWidth:90 }}>
                <div style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#666', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:4 }}>{label}</div>
                <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'1.6rem', letterSpacing:'-0.02em' }}>{value}</div>
              </div>
            ))}
            {is_live && stream?.viewer_count > 0 && (
              <div style={{ background:'#111', border:'1px solid #ff4444', padding:'0.75rem 1.25rem', minWidth:90 }}>
                <div style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#666', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:4 }}>Watching</div>
                <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'1.6rem', letterSpacing:'-0.02em', color:'#ff4444' }}>{formatNumber(stream.viewer_count)}</div>
              </div>
            )}
          </div>
        </div>

        <button style={{ display:'flex', alignItems:'center', gap:8, background:'#fff', color:'#000', border:'none', padding:'0.6rem 1.2rem', fontFamily:font.mono, fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', cursor:'pointer', flexShrink:0 }} onClick={() => { const sl = sanitizeTwitchUsername(user?.login); if (sl) window.open(`https://twitch.tv/${sl}`, '_blank', 'noopener,noreferrer'); }}>
          <Tv size={13} /> VIEW ON TWITCH
        </button>
      </div>

      {/* BODY */}
      <div style={{ display:'grid', gridTemplateColumns:'300px 1fr' }} className="bgn-body">

        {/* LEFT */}
        <div style={{ borderRight:'1px solid #222', padding:'2rem', display:'flex', flexDirection:'column', gap:'1.5rem' }} className="bgn-left-col">

          {/* Bio */}
          <div style={cardStyle}>
            <div style={cardTitle}>// Bio</div>
            <p style={{ fontFamily:font.body, fontWeight:300, fontSize:'0.9rem', lineHeight:1.6, color:'#ccc' }}>
              {user?.description ? `"${user.description}"` : 'No bio set.'}
            </p>
            {channel?.tags?.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:'0.75rem' }}>
                {channel.tags.slice(0,5).map(tag => (
                  <span key={tag} style={{ border:'1px solid #333', color:'#aaa', fontSize:'0.65rem', fontFamily:font.mono, padding:'2px 8px' }}>{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Social Links */}
          <div style={cardStyle}>
            <div style={cardTitle}>// Connect</div>
            {activeSocials.length === 0 ? (
              <p style={{ fontFamily:font.body, fontWeight:300, fontSize:'0.8rem', color:'#444' }}>No social links added yet.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {activeSocials.map(([platform, url]) => {
                  const config = SOCIAL_CONFIG[platform];
                  const safeHref = safeUrl(url);
                  if (!config || !safeHref) return null;
                  return (
                    <a
                      key={platform}
                      href={safeHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bgn-social-link"
                      style={{ display:'flex', alignItems:'center', gap:10, background:'#0d0d0d', border:'1px solid #222', padding:'0.6rem 0.75rem', textDecoration:'none', transition:'all 0.15s' }}
                    >
                      {config.icon}
                      <span style={{ fontFamily:font.mono, fontSize:'0.7rem', color:'#ccc', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {config.label}
                      </span>
                      <ExternalLink size={11} color="#555" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Account */}
          <div style={cardStyle}>
            <div style={cardTitle}>// Account</div>
            {[['Member Since', formatDate(user?.created_at)], ['Broadcaster', channel?.broadcaster_type||'affiliate']].map(([label,value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'0.4rem 0', borderBottom:'1px solid #1a1a1a' }}>
                <span style={{ fontFamily:font.mono, fontSize:'0.65rem', color:'#555', textTransform:'uppercase', letterSpacing:'0.1em' }}>{label}</span>
                <span style={{ fontFamily:font.mono, fontSize:'0.75rem', color:'#ccc' }}>{value}</span>
              </div>
            ))}
          </div>

        </div>

        {/* RIGHT */}
        <div style={{ padding:'2rem', display:'flex', flexDirection:'column', gap:'2rem' }}>

          {/* Clips */}
          <div>
            <div style={{ ...sectionTitle, display:'flex', justifyContent:'space-between' }}>
              <span>// Clips</span>
              {featured_clip_ids?.length > 0 && <span style={{ fontSize:'0.6rem', color:'#444' }}>{featured_clip_ids.length} FEATURED</span>}
            </div>
            {sortedClips.length === 0 ? (
              <div style={{ background:'#111', border:'1px solid #222', padding:'3rem', textAlign:'center' }}>
                <Film size={32} color="#333" style={{ margin:'0 auto 0.5rem' }} />
                <p style={{ fontFamily:font.mono, fontSize:'0.7rem', color:'#444', letterSpacing:'0.1em' }}>NO CLIPS YET</p>
              </div>
            ) : (
              <>
                {featured_clip_ids?.length > 0 && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:8, marginBottom:12 }}>
                    {sortedClips.slice(0, featured_clip_ids.length).map((clip, i) => (
                      <div key={clip.id} className="bgn-clip-card" style={{ position:'relative', border:'1px solid #333', overflow:'hidden', cursor:'pointer', transition:'border-color 0.15s' }} onClick={() => setActiveClip(clip)}>
                        <img src={sanitizeImageUrl(clip.thumbnail_url)} alt={clip.title} style={{ width:'100%', display:'block', filter:'grayscale(40%)', transition:'filter 0.2s' }} />
                        <div style={{ position:'absolute', top:6, left:6, background:'#FFD700', color:'#000', fontFamily:font.mono, fontSize:'0.55rem', fontWeight:700, padding:'2px 7px', letterSpacing:'0.1em' }}>#{i+1} FEATURED</div>
                        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.2s', background:'rgba(0,0,0,0.4)' }} className="bgn-play-overlay">
                          <div style={{ background:'#fff', borderRadius:'50%', width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <Play size={16} color="#000" fill="#000" style={{ marginLeft:2 }} />
                          </div>
                        </div>
                        <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'linear-gradient(transparent,rgba(0,0,0,0.85))', padding:'1rem 0.5rem 0.4rem' }}>
                          <p style={{ fontFamily:font.body, fontSize:'0.7rem', color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', margin:0 }}>{clip.title}</p>
                          <p style={{ fontFamily:font.mono, fontSize:'0.55rem', color:'#aaa', margin:'2px 0 0' }}>{formatNumber(clip.view_count)} views</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sortedClips.length > (featured_clip_ids?.length||0) && (
                  <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                    {sortedClips.slice(featured_clip_ids?.length||0, (featured_clip_ids?.length||0)+8).map(clip => (
                      <div key={clip.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.5rem 0.75rem', background:'#0a0a0a', cursor:'pointer', borderBottom:'1px solid #111', gap:12 }} onClick={() => setActiveClip(clip)}>
                        <Play size={12} color="#444" fill="#444" style={{ flexShrink:0 }} />
                        <span style={{ fontFamily:font.body, fontSize:'0.85rem', color:'#ccc', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{clip.title}</span>
                        <span style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#555', flexShrink:0 }}>{formatNumber(clip.view_count)} views</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Last VOD */}
          {videos?.length > 0 && (
            <div>
              <div style={sectionTitle}>// Last Stream</div>
              {(() => {
                const v = videos[0];
                const thumb = sanitizeImageUrl(v.thumbnail_url?.replace('%{width}','640').replace('%{height}','360'));
                return (
                  <div className="bgn-vod" style={{ position:'relative', background:'#111', border:'1px solid #222', cursor:'pointer', overflow:'hidden' }} onClick={() => { const u = safeExternalUrl(v.url); if (u) window.open(u,'_blank','noopener,noreferrer'); }}>
                    {thumb && <img src={thumb} alt={v.title} style={{ width:'100%', display:'block', filter:'grayscale(80%)' }} />}
                    <div className="bgn-overlay" style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.2s' }}>
                      <ExternalLink size={32} color="#fff" />
                    </div>
                    <div style={{ padding:'0.75rem 1rem' }}>
                      <p style={{ fontFamily:font.body, fontSize:'0.9rem', marginBottom:4 }}>{v.title}</p>
                      <div style={{ display:'flex', gap:'1rem' }}>
                        <span style={{ fontFamily:font.mono, fontSize:'0.65rem', color:'#666' }}>{formatNumber(v.view_count)} views</span>
                        <span style={{ fontFamily:font.mono, fontSize:'0.65rem', color:'#666' }}>{formatDuration(v.duration)}</span>
                        <span style={{ fontFamily:font.mono, fontSize:'0.65rem', color:'#666' }}>{formatDate(v.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Top Categories */}
          {topCats.length > 0 && (
            <div>
              <div style={sectionTitle}>// Top Categories</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {topCats.map(({ name, count, pct }) => (
                  <div key={name} style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{ fontFamily:font.mono, fontSize:'0.7rem', color:'#aaa', width:110, flexShrink:0 }}>{name.toUpperCase()}</span>
                    <div style={{ flex:1, background:'#111', height:20, border:'1px solid #222' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:'#FFD700', transition:'width 1s ease' }} />
                    </div>
                    <span style={{ fontFamily:font.mono, fontSize:'0.65rem', color:'#666', width:30, textAlign:'right' }}>{count}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trophy Case */}
          <div>
            <div style={sectionTitle}>// Trophy Case</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))', gap:8 }}>
              {badges.filter(b => b.unlocked).map(b => (
                <div key={b.id} style={{ background:'#1a1400', border:'1px solid #FFD700', padding:'1rem 0.5rem', textAlign:'center' }}>
                  <div style={{ fontSize:'1.2rem', marginBottom:6 }}>{b.icon}</div>
                  <div style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#FFD700', letterSpacing:'0.05em', lineHeight:1.3 }}>{b.label.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {activeClip && <ClipPlayerModal clip={activeClip} onClose={() => setActiveClip(null)} />}
    </div>
  );
};

export default PublicProfilePage;